// src/pages/PolicyPage.tsx
import React from "react";
import { useTranslation, Trans } from "react-i18next";
import SiteFooter from "../components/SiteFooter";

// Keep the date in code so it’s not duplicated across locales.
// (Translate only the label.)
const LAST_UPDATED = "2025-12-18";

// TODO: fill these with your real legal info (or load from env/config)
const PROVIDER_NAME = "Andrei Cosmin Marica";
const PROVIDER_ADDRESS = "Via Petrazzo 18, Messina (ME), 98142, Italia";
const CONTACT_EMAIL = "assistenza@coslo.it";
const PROVIDER_IVA = "IT03858840832";

type SectionKey =
  | "whoWeAre"
  | "appliesTo"
  | "dataWeProcess"
  | "howWeUse"
  | "aiProcessing"
  | "tenantSeparation"
  | "messagingChannels"
  | "automationEscalation"
  | "googleCalendar"
  | "thirdParties"
  | "retention"
  | "deletion"
  | "security"
  | "transfers"
  | "children"
  | "rights"
  | "changes"
  | "questions";

const PolicyPage: React.FC = () => {
  const { t } = useTranslation();

  const sections: SectionKey[] = [
    "whoWeAre",
    "appliesTo",
    "dataWeProcess",
    "howWeUse",
    "aiProcessing",
    "tenantSeparation",
    "messagingChannels",
    "automationEscalation",
    "googleCalendar",
    "thirdParties",
    "retention",
    "deletion",
    "security",
    "transfers",
    "children",
    "rights",
    "changes",
    "questions",
  ];

  const renderList = (key: string) => {
    const items = t(key, { returnObjects: true }) as unknown;
    if (!Array.isArray(items)) return null;

    return (
      <ul className="policy-list">
        {items.map((txt, idx) => (
          <li key={idx}>{String(txt)}</li>
        ))}
      </ul>
    );
  };

  return (
    <div className="policy-page">
      <section className="lp-section">
        <div className="lp-container policy-container">
          <header className="policy-header">
            <p className="policy-kicker">{t("policy.hero.kicker")}</p>
            <h1 className="lp-section-title">{t("policy.hero.title")}</h1>

            <p className="lp-text policy-intro">{t("policy.hero.intro")}</p>

            <p className="lp-text">
              <strong>{t("policy.hero.lastUpdatedLabel")}:</strong> {LAST_UPDATED}
            </p>
          </header>

          <div className="policy-grid">
            {/* 1) Who we are */}
            <section className="policy-section" aria-labelledby="policy-who">
              <h2 id="policy-who" className="policy-subtitle">
                {t("policy.sections.whoWeAre.title")}
              </h2>
              <p className="lp-text">
                <strong>{t("policy.sections.whoWeAre.serviceLabel")}:</strong>{" "}
                {t("policy.sections.whoWeAre.serviceValue")}
                <br />
                <strong>{t("policy.sections.whoWeAre.providerLabel")}:</strong>{" "}
                {PROVIDER_NAME + ("(P.IVA: " + PROVIDER_IVA + ")")}
                <br />
                <strong>{t("policy.sections.whoWeAre.addressLabel")}:</strong>{" "}
                {PROVIDER_ADDRESS}
                <br />
                <strong>{t("policy.sections.whoWeAre.emailLabel")}:</strong>{" "}
                <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
              </p>
            </section>

            {/* Everything else driven by i18n keys */}
            {sections
              .filter((k) => k !== "whoWeAre")
              .map((k) => (
                <section key={k} className="policy-section">
                  <h2 className="policy-subtitle">{t(`policy.sections.${k}.title`)}</h2>

                  {/* Paragraphs */}
                  {(() => {
                    const ps = t(`policy.sections.${k}.paragraphs`, {
                      returnObjects: true,
                    }) as unknown;

                    if (!Array.isArray(ps)) return null;

                    return ps.map((p, idx) => {
                      // Use <Trans> only where we need links or strong formatting.
                      const transKey = `policy.sections.${k}.paragraphs.${idx}`;
                      const needsTrans = t(`policy.sections.${k}.useTrans`, {
                        defaultValue: "false",
                      }) === "true";

                      if (!needsTrans) {
                        return (
                          <p key={idx} className="lp-text">
                            {t(transKey)}
                          </p>
                        );
                      }

                      return (
                        <p key={idx} className="lp-text">
                          <Trans
                            i18nKey={transKey}
                            values={{ email: CONTACT_EMAIL }}
                            components={{
                              a: <a href={`mailto:${CONTACT_EMAIL}`} />,
                              strong: <strong />,
                            }}
                          />
                        </p>
                      );
                    });
                  })()}

                  {/* Optional list */}
                  {renderList(`policy.sections.${k}.list`)}
                </section>
              ))}
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
};

export default PolicyPage;
